import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Search, ShieldBan, Trash2, UserRoundCheck } from "lucide-react";
import { getModeratorToken } from "../lib/dailyNewsApi";

const API = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_LABELS = {
  active: "Attivo",
  suspended: "Sospeso",
  deleted: "Eliminato",
};

function moderatorHeaders() {
  return { "x-moderator-token": getModeratorToken() };
}

function StatusBadge({ status }) {
  const classes = status === "active"
    ? "border-emerald-600 text-emerald-700 bg-emerald-50"
    : status === "suspended"
      ? "border-amber-600 text-amber-700 bg-amber-50"
      : "border-primary text-primary bg-red-50";
  return (
    <span className={`rounded-full border px-3 py-1 text-xs font-bold ${classes}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function ConfirmAction({ user, action, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const labels = {
    suspend: "Sospendi profilo",
    reactivate: "Riattiva profilo",
    delete: "Elimina definitivamente",
  };
  const submit = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(
        `${API}/api/v1/admin/users/${encodeURIComponent(user.identityId)}/action`,
        { action, reason: reason.trim() },
        { headers: { ...moderatorHeaders(), "Content-Type": "application/json" }, timeout: 10000 },
      );
      onDone();
    } catch (err) {
      setError(err?.response?.data?.message || "Operazione non riuscita.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/45 flex items-end sm:items-center justify-center p-3" role="dialog" aria-modal="true">
      <section className="card w-full max-w-lg p-5 border-t-4 border-t-primary">
        <h3 className="text-xl">{labels[action]}</h3>
        <p className="text-sm text-muted mt-2">
          Profilo: <strong className="text-ink">{user.displayName}</strong><br />
          ID: <span className="font-mono text-xs">{user.identityId}</span>
        </p>
        {action === "delete" && (
          <div className="mt-3 rounded-xl border border-primary bg-red-50 p-3 text-sm text-primary">
            Il profilo verrà anonimizzato e non potrà più effettuare azioni. I messaggi e i commenti pubblici resteranno visibili fino alla scadenza ordinaria, ma potranno essere rimossi in qualunque momento dalla Gestione contenuti.
          </div>
        )}
        <label className="block mt-4 text-xs uppercase tracking-wider font-bold text-muted" htmlFor="admin-action-reason">
          Motivazione obbligatoria
        </label>
        <textarea
          id="admin-action-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={500}
          className="mt-2 w-full min-h-28 rounded-xl border border-edge px-3 py-2 bg-white"
          placeholder="Indica il motivo del provvedimento"
        />
        {error && <p className="mt-2 text-sm text-primary">{error}</p>}
        <div className="flex flex-wrap justify-end gap-2 mt-4">
          <button type="button" className="rounded-full border border-edge px-4 py-2" onClick={onClose} disabled={busy}>Annulla</button>
          <button
            type="button"
            className="rounded-full bg-primary text-white px-4 py-2 disabled:opacity-40"
            onClick={submit}
            disabled={busy || reason.trim().length < 3}
          >
            {busy ? "Operazione in corso…" : "Conferma"}
          </button>
        </div>
      </section>
    </div>
  );
}

function UserCard({ user, onAction }) {
  return (
    <article className="card p-4 sm:p-5">
      <div className="flex gap-3 items-start">
        <div className="w-12 h-12 rounded-full border border-edge bg-cream overflow-hidden shrink-0 flex items-center justify-center font-bold text-primary">
          {user.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" /> : user.displayName?.slice(0, 1)?.toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap gap-2 items-center">
            <h3 className="text-lg truncate">{user.displayName}</h3>
            <StatusBadge status={user.status} />
          </div>
          <p className="text-xs text-muted font-mono mt-1 break-all">{user.identityId}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
            <div className="rounded-xl border border-edge bg-cream p-2 text-center"><strong>{user.postsCount}</strong><span className="block text-xs text-muted">Messaggi</span></div>
            <div className="rounded-xl border border-edge bg-cream p-2 text-center"><strong>{user.followersCount}</strong><span className="block text-xs text-muted">Follower</span></div>
            <div className="rounded-xl border border-edge bg-cream p-2 text-center"><strong>{user.followingCount}</strong><span className="block text-xs text-muted">Seguiti</span></div>
            <div className="rounded-xl border border-edge bg-cream p-2 text-center"><strong>{user.reportsCount}</strong><span className="block text-xs text-muted">Segnalazioni</span></div>
          </div>
        </div>
      </div>
      {user.status !== "deleted" && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-edge">
          {user.status === "active" ? (
            <button type="button" onClick={() => onAction(user, "suspend")} className="rounded-full border border-amber-600 text-amber-700 px-3 py-2 text-sm flex items-center gap-2">
              <ShieldBan size={16} /> Sospendi
            </button>
          ) : (
            <button type="button" onClick={() => onAction(user, "reactivate")} className="rounded-full border border-emerald-600 text-emerald-700 px-3 py-2 text-sm flex items-center gap-2">
              <UserRoundCheck size={16} /> Riattiva
            </button>
          )}
          <button type="button" onClick={() => onAction(user, "delete")} className="rounded-full border border-primary text-primary px-3 py-2 text-sm flex items-center gap-2 ml-auto">
            <Trash2 size={16} /> Elimina
          </button>
        </div>
      )}
    </article>
  );
}

export function AdminUsersPanel() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [pendingAction, setPendingAction] = useState(null);

  const load = useCallback(async () => {
    setState("loading");
    setError("");
    try {
      const response = await axios.get(`${API}/api/v1/admin/users`, {
        headers: moderatorHeaders(),
        params: { q: query.trim(), status, limit: 100 },
        timeout: 10000,
      });
      const payload = response.data;
      setItems(Array.isArray(payload) ? payload : (payload?.items || []));
      setState("ready");
    } catch (err) {
      setError(err?.response?.status === 401 ? "Credenziale amministrativa non valida." : "Elenco utenti non disponibile.");
      setState("error");
    }
  }, [query, status]);

  useEffect(() => {
    const timer = window.setTimeout(load, 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  return (
    <section>
      <div className="flex items-start gap-3 mb-4">
        <UserRoundCheck className="text-primary shrink-0" size={22} />
        <div>
          <h2 className="text-xl">Gestione utenti</h2>
          <p className="text-xs text-muted">Ricerca profili e applica provvedimenti motivati.</p>
        </div>
      </div>
      <div className="card p-3 sm:p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <label className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="w-full rounded-full border border-edge bg-white pl-10 pr-4 py-2.5"
              placeholder="Cerca per nome o ID"
            />
          </label>
          <select value={status} onChange={(event) => setStatus(event.target.value)} className="rounded-full border border-edge bg-white px-4 py-2.5">
            <option value="all">Tutti gli stati</option>
            <option value="active">Attivi</option>
            <option value="suspended">Sospesi</option>
            <option value="deleted">Eliminati</option>
          </select>
        </div>
      </div>
      {state === "loading" && <div className="card p-6 text-center text-muted">Caricamento utenti…</div>}
      {error && <div className="card p-4 text-primary flex gap-2"><AlertTriangle size={18} /> {error}</div>}
      {state === "ready" && items.length === 0 && <div className="card p-6 text-center text-muted">Nessun utente trovato.</div>}
      <div className="space-y-3">
        {items.map((user) => <UserCard key={user.identityId} user={user} onAction={(selected, action) => setPendingAction({ user: selected, action })} />)}
      </div>
      {pendingAction && (
        <ConfirmAction
          user={pendingAction.user}
          action={pendingAction.action}
          onClose={() => setPendingAction(null)}
          onDone={() => { setPendingAction(null); load(); }}
        />
      )}
      {state === "ready" && items.length > 0 && (
        <div className="mt-4 text-xs text-muted flex items-center gap-2"><CheckCircle2 size={15} /> {items.length} profili visualizzati</div>
      )}
    </section>
  );
}
